import ExtraCostPaymentType from "@/enums/ExtraCostPaymentType";
import SafetyDepositPaymentType from "@/enums/SafetyDepositPaymentType";
import type { ExtraCost } from "./PriceDetails";

type PaymentType = ExtraCostPaymentType | SafetyDepositPaymentType;

interface UsePriceDetailsProps {
    readonly showPayUponArrivalAsterisk: boolean;
    readonly excludedCosts?: ExtraCost[];
    readonly afterStayCosts?: ExtraCost[];
    readonly depositType?: SafetyDepositPaymentType;
}

interface PriceDetailsHookResult {
    renderAsterisk: (paymentType: PaymentType) => string;
    shouldShowAsteriskExplanation: boolean;
}

function usePriceDetails({
    afterStayCosts,
    depositType,
    excludedCosts,
    showPayUponArrivalAsterisk,
}: UsePriceDetailsProps): PriceDetailsHookResult {
    const renderAsterisk = (paymentType: PaymentType): string => {
        if (!showPayUponArrivalAsterisk) {
            return "";
        }

        const paymentTypesRequiringAsterisk = [
            ExtraCostPaymentType.ON_ARRIVAL_COSTS,
            ExtraCostPaymentType.AFTER_STAY_COSTS,
            ExtraCostPaymentType.OPTIONAL_ON_ARRIVAL_COSTS,
            SafetyDepositPaymentType.VIA_OWNER,
        ];

        return paymentTypesRequiringAsterisk.includes(paymentType) ? "*" : "";
    };

    const shouldShowAsteriskExplanation = (): boolean => {
        if (
            !showPayUponArrivalAsterisk ||
            (!excludedCosts && !afterStayCosts && !depositType)
        ) {
            return false;
        }

        const paymentTypes = [
            ...(excludedCosts?.map((cost) => cost.paymentType) || []),
            ...(afterStayCosts?.map((cost) => cost.paymentType) || []),
            depositType,
        ].filter(Boolean) as PaymentType[];

        return paymentTypes.some(
            (paymentType) => renderAsterisk(paymentType) === "*"
        );
    };

    return {
        renderAsterisk,
        shouldShowAsteriskExplanation: shouldShowAsteriskExplanation(),
    };
}

export default usePriceDetails;
